import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

import { MdManageAccounts } from "react-icons/md";
import { FiCheckCircle } from 'react-icons/fi';

import { Truck } from "lucide-react";

const setMobileViewport = () => {
    let viewport = document.querySelector("meta[name=viewport]");

    if (!viewport) {
        viewport = document.createElement("meta");
        viewport.name = "viewport";
        document.head.appendChild(viewport);
    }

    viewport.setAttribute(
        "content",
        "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover"
    );
};

const resetViewport = () => {
    const viewport = document.querySelector("meta[name=viewport]");

    if (viewport) {
        viewport.setAttribute(
            "content",
            "width=device-width, initial-scale=1"
        );
    }
};


const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
};

const DriverDashboard = () => {
    const { userProfile } = useAuth();
    const navigate = useNavigate();
    const { companySlug } = useParams();

    const go = (route) => navigate(`/${companySlug}/${route}`);

    useEffect(() => {
        setMobileViewport();

        return () => {
            resetViewport();
        };
    }, []);

    const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';

    const firstName = capitalize(
        userProfile?.full_name?.split(' ')[0] || userProfile?.username || ''
    );

    const roleBadgeText = [
        userProfile?.position?.toLowerCase() !== 'driver' ? userProfile?.position : null,
        userProfile?.role ? capitalize(userProfile.role.replace(/_/g, ' ')) : null,
    ].filter(Boolean).join(' · ');

    const ACTIONS = [
        {
            id: 'my-deliveries',
            route: 'my-deliveries',
            Icon: Truck,
            label: 'My Deliveries',
            desc: 'View your active deliveries',
        },
        {
            id: 'completed-deliveries',
            route: 'my-deliveries/completed',
            Icon: FiCheckCircle,
            label: 'Completed Deliveries',
            desc: 'View your delivery history',
        },
        {
            id: 'users',
            route: 'users',
            Icon: MdManageAccounts,
            label: 'Team',
            desc: 'View team members',
        },
    ];

    return (
        <div className="mobile-dashboard-page space-y-8">

            {/* ── Header ── */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 pb-6 border-b border-gray-200 dark:border-zinc-700">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                        {getGreeting()},{' '}
                        <span className="text-blue-600 dark:text-blue-400">{firstName}</span>
                    </h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {new Date().toLocaleDateString('en-AU', {
                            weekday: 'long',
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                        })}
                    </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="flex items-center gap-1.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        <span className="text-xs text-gray-500 dark:text-gray-400">Online</span>
                    </div>
                    <div className="px-2.5 py-1 rounded-md text-xs font-medium border bg-gray-50 dark:bg-gray-900/20 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-800">
                        {roleBadgeText}
                    </div>
                </div>
            </div>

            {/* ── Quick Actions ── */}
            <div>
                <span className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3 block">
                    Quick Actions
                </span>
                <div
                    className="grid gap-3"
                    style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}
                >
                    {ACTIONS.map(({ id, route, Icon, label, desc }) => (
                        <button
                            key={id}
                            onClick={() => go(route)}
                            className="group text-left bg-white dark:bg-zinc-800/60 hover:bg-gray-50 dark:hover:bg-zinc-800 border border-gray-200/80 dark:border-zinc-700/60 hover:border-blue-200 dark:hover:border-blue-800/60 rounded-xl p-4 transition-all duration-150"
                        >
                            <div className="flex items-center gap-3 pointer-events-none">
                                <div className="w-9 h-9 bg-gray-100 dark:bg-zinc-700 group-hover:bg-blue-50 dark:group-hover:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors">
                                    <Icon className="w-4 h-4 text-gray-500 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" />
                                </div>
                                <div className="min-w-0">
                                    <div className="text-sm font-medium text-gray-900 dark:text-white">{label}</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{desc}</div>
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            </div>

        </div>
    );
};

export default DriverDashboard;
